import React, { useEffect, useState } from "react";
import { useUser } from "../../contexts/UserContext";
import { useQuery } from "@tanstack/react-query";
import LoadingState from "../common/LoadingState";
import ErrorState from "../common/ErrorState";
import {
  Frequency,
  RecurringTransaction,
} from "../../domain/types/RecurringTransaction";
import { fetchRecurringTransactions } from "../../domain/services/transactions/fetchRecurringTransactions";
import { Alert } from "flowbite-react";
import { formatCurrency } from "../../utils/formatCurrency";
import { GoogleCalendarService } from "../../domain/services/googleCalendarService";
import { FirestoreAppUserRepository } from "../../infrastructure/repositories/appUserRepository";
import { PageHeader } from "../PageHeader";
import { RecurringTransactionDetails } from "./components/RecurringTransactionDetails";
import { RecurringTransactionCard } from "./components/RecurringTransactionCard";

function getRecurrenceRule(frequency: Frequency): string {
  switch (frequency) {
    case "WEEKLY":
      return "RRULE:FREQ=WEEKLY";
    case "BIWEEKLY":
      return "RRULE:FREQ=WEEKLY;INTERVAL=2";
    case "SEMI_MONTHLY":
      // Semi-monthly is not directly supported, so we can set two monthly recurrences
      return "";
    case "MONTHLY":
      return "RRULE:FREQ=MONTHLY";
    case "ANNUALLY":
      return "RRULE:FREQ=YEARLY";
    default:
      return "";
  }
}

const RecurringTransactionsList: React.FC = () => {
  const {
    currentUser,
    providerUser,
    appUser,
    loading: userLoading,
  } = useUser();
  const [isCalendarConnected, setIsCalendarConnected] =
    useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [addedEvents, setAddedEvents] = useState<string[]>([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [activeTransaction, setActiveTransaction] =
    useState<RecurringTransaction | null>(null);

  const googleCalendarService = GoogleCalendarService.getInstance();

  const {
    data: recurringTransactions,
    isLoading,
    error: queryError,
  } = useQuery<RecurringTransaction[]>({
    queryKey: ["recurringTransactions"],
    queryFn: async () => {
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      const accessToken = await currentUser.getIdToken();
      return await fetchRecurringTransactions(accessToken);
    },
    enabled: !!currentUser && !userLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (providerUser) {
      const isConnected =
        googleCalendarService.isCalendarConnected(providerUser);
      setIsCalendarConnected(isConnected);
    } else {
      setIsCalendarConnected(false);
    }
  }, [googleCalendarService, providerUser]);

  useEffect(() => {
    const fetchAddedEvents = async () => {
      if (appUser) {
        const appUserRepository = new FirestoreAppUserRepository();
        const events = await appUserRepository.getCalendarEvents(
          appUser.appUserId,
        );
        const streamIds = events ? Object.keys(events) : [];

        setAddedEvents(streamIds);
      }
    };
    fetchAddedEvents();
  }, [appUser]);

  if (userLoading || isLoading) {
    return <LoadingState message="Loading recurring transactions..." />;
  }

  if (queryError || error) {
    return <ErrorState error={(queryError || error) as Error} />;
  }

  if (
    !recurringTransactions ||
    recurringTransactions.length === 0 ||
    !appUser
  ) {
    return (
      <div className="p-8">
        <p className="text-center text-gray-500">
          No recurring transactions found.
        </p>
      </div>
    );
  }

  const handleAddToCalendar = async (transaction: RecurringTransaction) => {
    try {
      if (!providerUser) {
        const err = new Error("User data not available. Please try again.");
        console.error(err);
        setError(err);
        return;
      }

      const recurrenceRule = getRecurrenceRule(transaction.frequency);
      const amount = formatCurrency(
        transaction.last_amount.amount,
        transaction.last_amount.iso_currency_code ||
          transaction.last_amount.unofficial_currency_code ||
          "USD",
      );
      const eventDescription = `
${transaction.merchant_name || transaction.description}
Amount: ${amount}
Added by OmniButler
Link: https://www.omnibutler.ai
`;
      const event = {
        summary: `${transaction.description} - ${amount}`,
        description: eventDescription.trim(),
        start: {
          date: transaction.predicted_next_date,
        },
        end: {
          date: transaction.predicted_next_date,
        },
        recurrence: recurrenceRule ? [recurrenceRule] : undefined,
        reminders: {
          useDefault: false,
          overrides: [
            { method: "popup", minutes: 60 * 24 }, // 1 day before
            { method: "popup", minutes: 60 }, // 1 hour before
          ],
        },
      };
      console.debug("event: ", event);

      const token = await currentUser?.getIdToken();

      // Add event to Google Calendar via the service
      const result = await googleCalendarService.addEventToCalendar(
        event,
        appUser.appUserId,
        token,
      );

      if (result.success && result.event?.id) {
        console.debug("Event added to Google Calendar.");
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);

        // Update Firestore to include this transaction's stream_id
        const appUserRepository = new FirestoreAppUserRepository();
        await appUserRepository.addCalendarEvent(
          appUser.appUserId,
          transaction.stream_id,
          result.event.id,
        );

        // Update local state to reflect the added event
        setAddedEvents((prev) => [...prev, transaction.stream_id]);
      } else {
        console.error("Failed to add event to Google Calendar.");
      }
    } catch (err) {
      console.error("Error adding to calendar:", err);
      setError(err as Error);
    }
  };

  const handleReAddEvent = async (transaction: RecurringTransaction) => {
    try {
      const confirmReAdd = window.confirm(
        "This event has already been added. Do you want to add it again?",
      );
      if (!confirmReAdd) return;

      if (!providerUser) {
        const err = new Error("User data not available. Please try again.");
        console.error(err);
        setError(err);
        return;
      }

      // Get the stored eventId from Firestore
      const appUserRepository = new FirestoreAppUserRepository();
      const eventId = await appUserRepository.getCalendarEventId(
        appUser.appUserId,
        transaction.stream_id,
      );

      const token = await currentUser?.getIdToken();

      if (eventId && token) {
        await googleCalendarService.deleteEvent(
          appUser.appUserId,
          eventId,
          token,
        );
        console.debug("Previous calendar event deleted.");

        await appUserRepository.removeCalendarEvent(
          appUser.appUserId,
          transaction.stream_id,
        );

        setAddedEvents((prev) =>
          prev.filter((id) => id !== transaction.stream_id),
        );
      }

      await handleAddToCalendar(transaction);
    } catch (error) {
      console.error("Error re-adding to calendar:", error);
      setError(error as Error);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-md shadow">
      <PageHeader
        title="Recurring Transactions"
        description="Manage your recurring payments and subscriptions"
      />

      {showSuccessAlert && (
        <Alert color="success" onDismiss={() => setShowSuccessAlert(false)}>
          Event added to Google Calendar.
        </Alert>
      )}

      <div className="p-6 space-y-4">
        {!recurringTransactions || recurringTransactions.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <p>No recurring transactions found.</p>
          </div>
        ) : (
          recurringTransactions.map((transaction) => (
            <RecurringTransactionCard
              key={transaction.stream_id}
              transaction={transaction}
              isCalendarConnected={isCalendarConnected}
              isAddedToCalendar={addedEvents.includes(transaction.stream_id)}
              onAddToCalendar={() => handleAddToCalendar(transaction)}
              onReAddToCalendar={() => handleReAddEvent(transaction)}
              onClick={() => setActiveTransaction(transaction)}
            />
          ))
        )}
      </div>

      {activeTransaction && (
        <RecurringTransactionDetails
          transaction={activeTransaction}
          onClose={() => setActiveTransaction(null)}
        />
      )}
    </div>
  );
};

export default RecurringTransactionsList;
